import { Button, Switch } from '@risksmart-app/atomic-ui';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { Edit02 } from '@untitled-ui/icons-react';
import _ from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { BasicModule } from 'src/context/moduleContext';

import { AppetiteCascadingSettings } from './AppetiteCascadingSettings';
import { IngestionConfigSettings } from './IngestionConfigSettings';
import { RiskScoreSettings } from './RiskScoreSettings';

// Maps a submodule key (as defined in `packages/modules/src/defaults.ts`) to the
// `Parent_Type_Enum` that actually owns the configurable tabs in Hasura's `tab`
// table. Most submodule keys do not correspond to a `Parent_Type_Enum` value —
// they describe a *capability* (e.g. `oversight_register_and_findings`), while
// the tab defaults are keyed by the underlying entity
// (`oversight_assessment`, set up in migration `1746609743782_tabs`).
//
// Any submodule with `allowTabConfig: true` MUST be added here, otherwise the
// "Edit tabs" link is suppressed (rather than rendering an empty modal — the
// RSP-5774 bug).
const SUBMODULE_KEY_TO_PARENT_TYPE: Record<string, ParentType> = {
  oversight_register_and_findings: Parent_Type_Enum.OversightAssessment,
};

const SubModuleSettings: FC<{
  subModules?: Record<string, BasicModule>;
  toggleItem: (id: string) => void;
  setShowTabSettings: (show: boolean) => void;
  setTabSettingsParentType: (type: ParentType | undefined) => void;
  moduleId: string;
  isDirty?: boolean;
}> = ({
  subModules,
  toggleItem,
  moduleId,
  setShowTabSettings,
  setTabSettingsParentType,
  isDirty,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'modules' });

  return (
    <>
      <h4 className={'text-lg'}>{t('submodules_title')}</h4>
      {_.map(subModules, (subModule: BasicModule, subKey) => {
        const tabParentType = SUBMODULE_KEY_TO_PARENT_TYPE[subKey];

        return (
          <div
            key={subKey}
            className={
              'flex border-0 border-grey150 border-solid border-t-[0.5px] pt-4'
            }
          >
            <div>
              <Switch
                checked={subModule.enabled}
                onCheckedChange={() =>
                  toggleItem(`${moduleId}.subModules.${subKey}`)
                }
                aria-label={`Toggle ${subKey}`}
              />
            </div>
            <div className={'ml-[8px]'}>
              <h4 className={'my-0 text-lg'}>
                {/* @ts-ignore */}
                {t(`titles.${subKey}`)}
              </h4>
              {/* @ts-ignore */}
              <p className={'text-base'}>{t(`descriptions.${subKey}`)}</p>
              {subKey === 'risk_scoring' && subModule.enabled && (
                <RiskScoreSettings />
              )}
              {subKey === 'appetite_cascading' && subModule.enabled && (
                <AppetiteCascadingSettings />
              )}
              {subKey === 'reg_feed' && subModule.enabled && (
                <IngestionConfigSettings />
              )}
              {subModule.allowTabConfig && tabParentType && (
                <>
                  <h4 className={'text-lg'}>{t('configuration')}</h4>
                  <Button
                    variant={'neutral'}
                    style={'outline'}
                    radius={'sm'}
                    className={'w-fit gap-2'}
                    disabled={isDirty}
                    title={isDirty ? t('unsaved_changes') : undefined}
                    onClick={() => {
                      setTabSettingsParentType(tabParentType);
                      setShowTabSettings(true);
                    }}
                  >
                    <Edit02 width={16} />
                    <span>{t('edit_tabs')}</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SubModuleSettings;
